import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/App';
import { useBranding } from '@/contexts/BrandingContext';
import { BrandMark } from '@/components/BrandLogo';
import { Button } from '@/components/ui/button';
import {
  Building2, Users, Briefcase, TrendingUp, ArrowRight, Heart, Target, ChevronRight, Phone, Mail
} from 'lucide-react';

const SERVICE_ICONS = [Users, Building2, TrendingUp];

export default function AboutPage() {
  const { login, isAuthenticated } = useAuth();
  const { branding } = useBranding();
  const about = branding?.about_us || {};

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--brand-background, #F5F4F0)', fontFamily: 'var(--font-body, Inter)' }}>
      {/* Navbar */}
      <nav className="bg-white border-b border-[var(--brand-border,#E7E5E4)] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2">
            <BrandMark onDark={false} className="w-10 h-10" alt={branding?.site_name || 'Estadía 365'} />
            <span className="font-bold text-xl" style={{ fontFamily: 'var(--font-heading, Outfit)', color: 'var(--brand-text-primary, #1C1917)' }}>
              {branding?.site_name || 'Estadía 365'}
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/properties" className="text-sm font-medium hover:opacity-80" style={{ color: 'var(--brand-text-secondary, #57534E)' }}>Propiedades</Link>
            <Link to="/faq" className="text-sm font-medium hover:opacity-80" style={{ color: 'var(--brand-text-secondary, #57534E)' }}>FAQ</Link>
            {!isAuthenticated && (
              <Button onClick={login} style={{ backgroundColor: 'var(--brand-primary, #264639)', color: '#fff' }}>Iniciar Sesión</Button>
            )}
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-20" style={{ backgroundColor: 'var(--brand-primary, #264639)' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="font-semibold uppercase tracking-[0.2em] text-sm mb-4 opacity-80" style={{ color: 'var(--brand-secondary, #C36F4D)' }}>
            Sobre Nosotros
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6" style={{ fontFamily: 'var(--font-heading, Outfit)' }} data-testid="about-hero-title">
            {about.hero_title || 'Estadía 365 conecta alojamientos con huéspedes durante todo el año'}
          </h1>
          <p className="text-lg text-white/80 max-w-3xl mx-auto leading-relaxed" data-testid="about-hero-desc">
            {about.hero_description || 'Somos una marca especializada en alojamientos temporarios, vacacionales y turísticos.'}
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          <p className="text-lg leading-relaxed" style={{ color: 'var(--brand-text-secondary, #57534E)' }} data-testid="about-story">
            {about.story || ''}
          </p>
          <p className="text-lg leading-relaxed" style={{ color: 'var(--brand-text-secondary, #57534E)' }} data-testid="about-story-2">
            {about.story_2 || ''}
          </p>
          <p className="text-lg leading-relaxed" style={{ color: 'var(--brand-text-secondary, #57534E)' }} data-testid="about-story-3">
            {about.story_3 || ''}
          </p>
        </div>
      </section>

      {/* Purpose */}
      <section className="py-16" style={{ backgroundColor: 'var(--brand-accent, #DCE9E2)' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="font-semibold uppercase tracking-[0.2em] text-xs mb-3" style={{ color: 'var(--brand-secondary, #C36F4D)' }}>
            Nuestro Proposito
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-8" style={{ fontFamily: 'var(--font-heading, Outfit)', color: 'var(--brand-text-primary, #1C1917)' }} data-testid="about-purpose-title">
            {about.purpose_title || 'Convertir alojamientos en experiencias confiables'}
          </h2>
          <div className="text-lg leading-relaxed whitespace-pre-line max-w-3xl mx-auto" style={{ color: 'var(--brand-text-secondary, #57534E)' }} data-testid="about-purpose-desc">
            {about.purpose_description || ''}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="font-semibold uppercase tracking-[0.2em] text-xs mb-3" style={{ color: 'var(--brand-secondary, #C36F4D)' }}>
              Que Hacemos
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {(about.services || []).map((svc, idx) => {
              const Icon = SERVICE_ICONS[idx % SERVICE_ICONS.length];
              return (
                <div key={`service-${svc.title}`} className="p-8 rounded-xl transition-all hover:shadow-lg hover:-translate-y-1" style={{ backgroundColor: 'var(--brand-background, #F5F4F0)' }} data-testid={`about-service-${idx}`}>
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-6" style={{ backgroundColor: 'var(--brand-accent, #DCE9E2)' }}>
                    <Icon className="w-7 h-7" style={{ color: 'var(--brand-primary, #264639)' }} />
                  </div>
                  <h3 className="text-xl font-bold mb-3" style={{ fontFamily: 'var(--font-heading, Outfit)', color: 'var(--brand-text-primary, #1C1917)' }}>
                    {svc.title}
                  </h3>
                  <p className="leading-relaxed" style={{ color: 'var(--brand-text-secondary, #57534E)' }}>
                    {svc.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16" style={{ backgroundColor: 'var(--brand-primary, #264639)' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4" style={{ fontFamily: 'var(--font-heading, Outfit)' }}>
            {branding?.cta_title || 'Tienes una propiedad?'}
          </h2>
          <p className="text-white/80 mb-8 max-w-2xl mx-auto">{branding?.cta_subtitle || ''}</p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to="/properties">
              <Button className="text-white px-6" style={{ backgroundColor: 'var(--brand-secondary, #C36F4D)' }}>
                Ver Alojamientos <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/faq">
              <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 px-6">
                Preguntas Frecuentes
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1C1917] py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <BrandMark onDark className="w-8 h-8" alt="" />
              <span className="text-white font-semibold" style={{ fontFamily: 'var(--font-heading, Outfit)' }}>{branding?.site_name || 'Estadía 365'}</span>
            </div>
            <div className="flex gap-6 text-sm">
              <Link to="/" className="text-[#78716C] hover:text-white transition-colors">Inicio</Link>
              <Link to="/properties" className="text-[#78716C] hover:text-white transition-colors">Propiedades</Link>
              <Link to="/faq" className="text-[#78716C] hover:text-white transition-colors">FAQ</Link>
            </div>
            <div className="flex gap-4">
              {branding?.contact?.phone && (
                <a href={`tel:${branding.contact.phone}`} className="text-[#78716C] hover:text-white"><Phone className="w-4 h-4" /></a>
              )}
              {branding?.contact?.email && (
                <a href={`mailto:${branding.contact.email}`} className="text-[#78716C] hover:text-white"><Mail className="w-4 h-4" /></a>
              )}
            </div>
          </div>
          <div className="border-t border-[#333] mt-6 pt-6 text-center">
            <p className="text-[#78716C] text-xs">{branding?.footer_text || '© Estadia 365'}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
